import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import {
  List, ChevronLeft, ChevronRight, Edit3, Trash2, Check, X, Loader2
} from 'lucide-react';
import { getMyPriceLists, updateMyPriceList, deleteMyPriceList } from '../../services/api';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ListasPrecios = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [isActive, setIsActive] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (isActive !== '') params.is_active = isActive === 'true';
      const res = await getMyPriceLists(params);
      setData(res.data);
    } catch { toast.error('Error cargando listas de precios'); }
    finally { setLoading(false); }
  }, [page, isActive]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, is_default: item.is_default, is_active: item.is_active });
  };

  const saveEdit = async () => {
    try {
      await updateMyPriceList(editingId, editForm);
      toast.success('Lista actualizada');
      setEditingId(null);
      load();
    } catch { toast.error('Error al actualizar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar esta lista de precios?')) return;
    try {
      await deleteMyPriceList(id);
      toast.success('Lista desactivada');
      load();
    } catch { toast.error('Error al desactivar'); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <List className="w-7 h-7 text-teal-600" />
          Listas de Precios
        </h1>
        <p className="text-slate-500 mt-1">Listas de precios sincronizadas desde tu POS</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <select value={isActive} onChange={e => { setIsActive(e.target.value); setPage(1); }}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Todas</option>
            <option value="true">Activas</option>
            <option value="false">Inactivas</option>
          </select>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-center px-4 py-3">Default</th>
                <th className="text-center px-4 py-3">Activa</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-center px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-400">No hay listas de precios</td></tr>
              )}
              {data.items.map(item => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {editingId === item.id ? (
                      <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                        className="border rounded px-2 py-1 text-sm w-full" />
                    ) : (
                      <span className="font-medium text-slate-700">{item.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingId === item.id ? (
                      <input type="checkbox" checked={editForm.is_default}
                        onChange={e => setEditForm({ ...editForm, is_default: e.target.checked })} />
                    ) : (
                      item.is_default
                        ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Default</span>
                        : <span className="text-slate-400">\u2014</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingId === item.id ? (
                      <input type="checkbox" checked={editForm.is_active}
                        onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} />
                    ) : (
                      item.is_active
                        ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Activa</span>
                        : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Inactiva</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{item.source || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(item.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    {editingId === item.id ? (
                      <div className="flex gap-1 justify-center">
                        <button onClick={saveEdit} className="p-1 rounded hover:bg-green-100 text-green-600">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded hover:bg-slate-100 text-slate-400">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => startEdit(item)} className="p-1 rounded hover:bg-slate-100 text-slate-400">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {item.is_active && (
                          <button onClick={() => handleDelete(item.id)} className="p-1 rounded hover:bg-red-100 text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </DashboardLayout>
  );
};

export default ListasPrecios;
